import { lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { ThemeProvider, AuthProvider } from "@/context/AppContext";
import Layout from "@/components/site/Layout";
import Home from "@/pages/Home";
import TechLogin from "@/pages/tech/Login";
import TechHome from "@/pages/tech/Home";
import TechJob from "@/pages/tech/Job";

const Services = lazy(() => import("@/pages/Services"));
const ServiceDetail = lazy(() => import("@/pages/ServiceDetail"));
const Book = lazy(() => import("@/pages/Book"));
const Track = lazy(() => import("@/pages/Track"));
const Partner = lazy(() => import("@/pages/Partner"));
const PinFinder = lazy(() => import("@/pages/PinFinder"));
const Cities = lazy(() => import("@/pages/Cities"));
const CityDetail = lazy(() => import("@/pages/CityDetail"));
const About = lazy(() => import("@/pages/About"));
const Warranty = lazy(() => import("@/pages/Warranty"));
const Help = lazy(() => import("@/pages/Help"));
const Contact = lazy(() => import("@/pages/Contact"));
const Privacy = lazy(() => import("@/pages/Privacy"));
const Terms = lazy(() => import("@/pages/Terms"));
const Blogs = lazy(() => import("@/pages/Blogs"));
const BlogPost = lazy(() => import("@/pages/BlogPost"));
const Enquiry = lazy(() => import("@/pages/Enquiry"));
const Offers = lazy(() => import("@/pages/Offers"));
const ServiceAreas = lazy(() => import("@/pages/ServiceAreas"));
const TechnicianApp = lazy(() => import("@/pages/TechnicianApp"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const AdminLogin = lazy(() => import("@/pages/AdminLogin"));
const AdminForgotPassword = lazy(() => import("@/pages/AdminForgotPassword"));
const AdminResetPassword = lazy(() => import("@/pages/AdminResetPassword"));
const Admin = lazy(() => import("@/pages/admin/Admin"));
const TechForgot = lazy(() => import("@/pages/tech/Forgot"));
const TechReset = lazy(() => import("@/pages/tech/Reset"));

const Fallback = () => (
  <div className="min-h-[60vh] grid place-items-center" data-testid="page-loading">
    <div className="h-10 w-10 rounded-full border-4 border-primary/20 border-t-primary animate-spin" />
  </div>
);

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <div className="App">
          <BrowserRouter>
            <Suspense fallback={<Fallback />}>
              <Routes>
                <Route element={<Layout />}>
                  <Route path="/" element={<Home />} />
                  <Route path="/services" element={<Services />} />
                  <Route path="/services/:slug" element={<ServiceDetail />} />
                  <Route path="/services/:slug/:city" element={<ServiceDetail />} />
                  <Route path="/book" element={<Book />} />
                  <Route path="/track" element={<Track />} />
                  <Route path="/partner" element={<Partner />} />
                  <Route path="/pincode-finder" element={<PinFinder />} />
                  <Route path="/cities" element={<Cities />} />
                  <Route path="/cities/:slug" element={<CityDetail />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/warranty" element={<Warranty />} />
                  <Route path="/help" element={<Help />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/privacy" element={<Privacy />} />
                  <Route path="/terms" element={<Terms />} />
                  <Route path="/blogs" element={<Blogs />} />
                  <Route path="/blogs/:slug" element={<BlogPost />} />
                  <Route path="/offers" element={<Offers />} />
                  <Route path="/service-areas" element={<ServiceAreas />} />
                  <Route path="/technician-app" element={<TechnicianApp />} />
                  {["careers", "franchise", "refer-and-earn", "corporate-services"].map((k) => <Route key={k} path={`/${k}`} element={<Enquiry kind={k} />} />)}
                  <Route path="*" element={<NotFound />} />
                </Route>
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route path="/admin/forgot-password" element={<AdminForgotPassword />} />
                <Route path="/admin/reset-password" element={<AdminResetPassword />} />
                <Route path="/admin/*" element={<Admin />} />
                <Route path="/tech/login" element={<TechLogin />} />
                <Route path="/tech/forgot-password" element={<TechForgot />} />
                <Route path="/tech/reset-password" element={<TechReset />} />
                <Route path="/tech" element={<TechHome />} />
                <Route path="/tech/jobs/:id" element={<TechJob />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
          <Toaster richColors position="top-center" />
        </div>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
